import routes from 'js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import { WorkId } from 'js/liswp/types';

// Store click timestamps keyed by work ID
const clickTimestamps = new Map<WorkId, number>();

// Store pending metrics to batch into a single request
const pendingMetrics = new Map<WorkId, { modal?: number; preview?: number }>();

const packRowsOverlapReported = new Set<WorkId>();

export function reportPackRowsOverlap(
  workId: WorkId,
  {
    rendered,
    overlap,
    packState,
  }: {
    rendered: number;
    overlap: number;
    packState: string;
  },
): void {
  if (packRowsOverlapReported.has(workId)) {
    return;
  }
  packRowsOverlapReported.add(workId);

  fetchWithCsrf(
    routes.timing_api_v0_work_bulk_download_counts_path({
      work_id: workId,
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pack_rows_rendered: rendered,
        pack_rows_overlap: overlap,
        pack_rows_pack_state: packState,
      }),
    },
  ).catch((error) => {
    console.error('Failed to report LISWP pack-row overlap:', error);
  });
}

/**
 * Records the timestamp when a user clicks a download or bulk download button.
 * This timestamp will be used to calculate latency to modal/preview impressions.
 */
export function recordClickTimestamp(workId: WorkId): void {
  clickTimestamps.set(workId, performance.now());
}

/**
 * Sends batched metrics to the backend.
 */
function sendMetrics(
  workId: WorkId,
  metrics: { modal?: number; preview?: number },
): void {
  const body: { modal_latency_ms?: number; preview_latency_ms?: number } = {};

  if (metrics.modal !== undefined) {
    body.modal_latency_ms = metrics.modal;
  }

  if (metrics.preview !== undefined) {
    body.preview_latency_ms = metrics.preview;
  }

  fetchWithCsrf(
    routes.timing_api_v0_work_bulk_download_counts_path({
      work_id: workId,
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  ).catch((error) => {
    console.error('Failed to report bulk download timing metrics:', error);
  });
}

/**
 * Reports the latency from button click to bulk download modal impression.
 * Batches the metric to send with preview impression.
 */
export function reportModalImpression(workId: WorkId): void {
  const clickTime = clickTimestamps.get(workId);
  if (clickTime === undefined) {
    return; // No click timestamp recorded, skip reporting
  }

  const latencyMs = performance.now() - clickTime;
  const metrics = pendingMetrics.get(workId) || {};
  metrics.modal = latencyMs;
  pendingMetrics.set(workId, metrics);
}

/**
 * Reports the latency from button click to bundle preview impression.
 * Sends batched metrics (modal + preview) to the backend and clears state.
 */
export function reportPreviewImpression(workId: WorkId): void {
  const clickTime = clickTimestamps.get(workId);
  if (clickTime === undefined) {
    return; // No click timestamp recorded, skip reporting
  }

  const latencyMs = performance.now() - clickTime;
  const metrics = pendingMetrics.get(workId) || {};
  metrics.preview = latencyMs;

  // Send batch when preview completes (last event)
  sendMetrics(workId, metrics);

  // Clean up
  pendingMetrics.delete(workId);
  clickTimestamps.delete(workId);
}
